import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Grid,
  Stack,
  Table,
  Avatar,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import ApiIcon from "@mui/icons-material/Api";
import moment from "moment";
//Srcs
import "../../Style/pagestyle.scss";
import EmptyData from "../../Components/Include/EmptyData/EmptyData";
import LoadingPage from "../../Utils/LoadingPage";
import InvoiceAction from "../../Components/Invoice/InvoiceAction";
import FooterPagination from "../../Utils/FooterPagination";
import useWithDimension from "../../Hooks/useWithDimension";
import useGetInvoicePagination from "../../Hooks/useGetInvoicePagination";
import { AuthContext } from "../../Context/AuthContext";
import { SearchTextField, ButtonCreate } from "../../Utils/Component";
import {
  SelectStudent,
  SelectEnrollmentByStudent,
} from "../../Components/Include/DynamicSelect/DynamicSelect";
import {
  currencyFormat,
  generateInvoiceNumber,
} from "../../Function/DynamicFn";
import PrintMultiInvoice from "../../Components/Invoice/Print/PrintMultiInvoice";

export default function OnlineInvoice() {
  // Change Language
  const { t } = useContext(AuthContext);

  const [openPrint, setOpenPrint] = useState(false);
  const handleOpenPrint = () => setOpenPrint(true);
  const handleClosePrint = () => setOpenPrint(false);

  // Filters state
  const [prepareData, setPrepareData] = useState([]);
  const [toPrintData, setToPrintData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("All");
  const [studentId, setStudentId] = useState({ id: "", title: "" });
  const [enrollmentId, setEnrollmentId] = useState({ id: "", title: "" });

  const {
    page,
    limit,
    setPage,
    refetch,
    loading,
    tableData,
    setLoading,
    handleLimit,
    handleSearch,
    paginationData,
  } = useGetInvoicePagination({
    keyword: "",
    type: "online",
    endDate: "",
    startDate: "",
    paymentStatus, // All Paid Owe
    studentId: studentId?.id,
    enrollmentId: enrollmentId?.id,
  });

  // Handle checkbox
  const handleCheckbox = (row) => {
    setSelectedIds((prev) =>
      prev.includes(row._id)
        ? prev.filter((id) => id !== row._id)
        : [...prev, row._id],
    );

    setToPrintData((prev) => {
      // If already exists → remove
      if (prev.some((item) => item._id === row._id)) {
        return prev.filter((item) => item._id !== row._id);
      }
      // Else → add
      return [...prev, row];
    });
  };

  // Update prepareData when tableData changes
  useEffect(() => {
    if (tableData?.length > 0) {
      setPrepareData(tableData);
    }
  }, [tableData]);

  useEffect(() => {
    if (studentId || enrollmentId || paymentStatus) {
      setPage(1);
    }
  }, [studentId, enrollmentId, paymentStatus]);

  // ======================= Resize width Screen ======================
  const { width } = useWithDimension();

  const translatePaymentStatus = (status) => {
    if (status === "Paid") return t("thead_paid");
    if (status === "Unpaid") return t("thead_unpaid");
    if (status === "Owe") return t("thead_owe");
    else return status;
  };

  return (
    <div className="page-container">
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("search")}
            </Typography>
            <SearchTextField
              className={"text-field"}
              placeholder={t("thead_invoice_id")}
              handleSearch={handleSearch}
            />
          </Grid>

          {/* Select student */}
          <Grid item xs={6} sm={6} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_student")}
            </Typography>
            <SelectStudent
              studentType="online"
              className={"text-field"}
              value={studentId}
              onChange={setStudentId}
              buttonAdd={false}
            />
          </Grid>

          {/* Select class */}
          {/* <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_class")}
            </Typography>

            <SelectEnrollmentByStudent
              studentId={studentId?.id}
              value={enrollmentId}
              onChange={setEnrollmentId}
              className={"text-field"}
            />
          </Grid> */}

          {/* Payment status */}
          <Grid item xs={6} sm={6} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_payment_status")}
            </Typography>
            <FormControl fullWidth size="small" className={"text-field"}>
              <Select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e?.target?.value)}
              >
                <MenuItem value={"All"}>All</MenuItem>
                <MenuItem value={"Paid"}>Paid</MenuItem>
                <MenuItem value={"Owe"}>Owe</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={6} md={3} lg={4.8} xl={6}>
            <ButtonCreate
              handleOpen={handleOpenPrint}
              classBtn={"btn-create"}
              classIcon={"icon-add"}
              title={t("thead_print") + ` (${selectedIds?.length})`}
            />

            {openPrint && (
              <PrintMultiInvoice
                open={openPrint}
                tableData={toPrintData}
                handleClose={handleClosePrint}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table" aria-label="simple table">
            <TableHead className="header-row">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width="5%"
                >
                  {t("no")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_invoice_id")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="25%">
                  {t("thead_student")} / {t("thead_other")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_date")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  align="center"
                  width="10%"
                >
                  {t("thead_status")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  align="right"
                  width="10%"
                >
                  {t("thead_owe_amount")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  align="right"
                  width="10%"
                >
                  {t("thead_total_amount")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  align="right"
                  width="10%"
                >
                  {/* {t("thead_total_amount")} */}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title-end")}
                  width="10%"
                ></TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableBody className="body">
                <TableRow className="body-row">
                  <TableCell
                    colSpan={10}
                    className="body-cell"
                    sx={{ bgcolor: "#fff" }}
                  >
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <>
                {tableData?.length === 0 || tableData?.length === undefined ? (
                  <TableBody className="body">
                    <TableRow className="body-row">
                      <TableCell colSpan={10} className="body-cell">
                        <EmptyData />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <>
                    <TableBody className="body">
                      {prepareData?.map((row, index) => {
                        // console.log("row::", row);
                        return (
                          <TableRow className="body-row" key={index}>
                            <TableCell
                              className={
                                row?.void
                                  ? "body-cell-start-complete"
                                  : "body-cell-start"
                              }
                            >
                              {index + paginationData?.slNo}
                            </TableCell>
                            <TableCell
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                              sx={{ color: "#131870 !important" }}
                            >
                              {generateInvoiceNumber(
                                row?.invoiceNumber,
                                row?.createdAt,
                              )}
                            </TableCell>

                            <TableCell
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                            >
                              <Stack direction="row" spacing={1.4}>
                                <Stack
                                  direction="column"
                                  justifyContent="center"
                                >
                                  <Avatar
                                    sx={{ width: 40, height: 40 }}
                                    alt={row?.studentId?.studentName}
                                    src={row?.studentId?.profile}
                                  />
                                </Stack>
                                <Stack
                                  direction="column"
                                  justifyContent="center"
                                >
                                  <Typography className="student-name">
                                    {row?.studentId?.familyName +
                                      " " +
                                      row?.studentId?.studentName}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </TableCell>

                            <TableCell
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                            >
                              {row?.date &&
                                moment(row?.date).format("DD-MMM-YYYY")}
                            </TableCell>

                            <TableCell
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                              align="center"
                            >
                              <Typography className={row?.paymentStatus}>
                                <ApiIcon
                                  sx={{
                                    paddingTop: "2px",
                                    marginRight: 0.7,
                                    fontSize: 14,
                                  }}
                                />
                                {translatePaymentStatus(row?.paymentStatus)}
                              </Typography>
                            </TableCell>

                            <TableCell
                              align="right"
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                              sx={{ color: "red" }}
                            >
                              {`$ ${currencyFormat(row?.totalOwe)}`}
                            </TableCell>

                            <TableCell
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                              align="right"
                              sx={{ color: "#131870" }}
                            >
                              $ {currencyFormat(row?.totalAmount || 0)}
                            </TableCell>

                            <TableCell
                              className={
                                row?.void ? "body-cell-complete" : "body-cell"
                              }
                              align="center"
                            >
                              <Checkbox
                                sx={{ padding: "2px !important" }}
                                checked={selectedIds.includes(row?._id)}
                                onChange={() => handleCheckbox(row)}
                              />
                            </TableCell>

                            <TableCell
                              className={
                                row?.void
                                  ? "body-cell-end-complete"
                                  : "body-cell-end"
                              }
                              align="right"
                            >
                              <InvoiceAction
                                editData={row}
                                setRefetch={refetch}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </>
                )}
              </>
            )}
          </Table>
        </TableContainer>

        {/* ==================== Pagination  ============================= */}
        <FooterPagination
          totalPages={paginationData?.totalPages}
          totalDocs={paginationData?.totalDocs}
          limit={limit}
          page={page}
          setPage={setPage}
          handleLimit={handleLimit}
          setLoading={setLoading}
        />
      </Box>
    </div>
  );
}
